import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';

export interface DialogChromeAction {
    label: string;
    onClick: () => void;
}

/**
 * Lets the dialog shell hand its close action down to whichever `AppDialogLayout`
 * ends up owning the header — including one rendered by the modal's *content*,
 * which the shell cannot reach through props.
 *
 * This is why the close lives in the header row rather than floating in the
 * corner: only the layout knows where the title's first line is, so only the
 * layout can align back, title and close to each other.
 *
 * Lives in its own module so `AppDialogLayout` never imports `modal-provider`
 * (which imports `AppDialogLayout` — that would be a cycle).
 */
const DialogChromeContext = createContext<{ close?: DialogChromeAction }>({});

export function DialogChromeProvider({
    close,
    children,
}: PropsWithChildren<{ close?: DialogChromeAction }>) {
    const value = useMemo(() => ({ close }), [close]);

    return <DialogChromeContext.Provider value={value}>{children}</DialogChromeContext.Provider>;
}

export const useDialogChrome = () => useContext(DialogChromeContext);
